import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const trimTrailingSlashes = (value) => {
  let end = value.length;

  while (end > 0 && value.charCodeAt(end - 1) === 47) {
    end -= 1;
  }

  return value.slice(0, end);
};

const SITE_URL = trimTrailingSlashes(String(process.env.REACT_APP_SITE_URL || "https://zealfc.com"));
const DEFAULT_TITLE = "ZealFC | Find and Join Football Games";
const DEFAULT_DESCRIPTION =
  "Discover upcoming football games, join matches, confirm attendance, and manage your next move with ZealFC.";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const DEFAULT_ROBOTS = "index, follow";

const isAbsoluteUrl = (value) => /^(https?:)?\/\//i.test(value || "");

const toAbsoluteUrl = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return SITE_URL;
  if (isAbsoluteUrl(normalized)) return normalized;
  if (normalized.startsWith("data:") || normalized.startsWith("blob:")) return normalized;
  return `${SITE_URL}${normalized.startsWith("/") ? normalized : `/${normalized}`}`;
};

const upsertMeta = (attribute, key, content) => {
  if (typeof document === "undefined") return;

  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  if (typeof document === "undefined") return;

  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const upsertStructuredData = (serializedStructuredData) => {
  if (typeof document === "undefined") return;

  const elementId = "route-structured-data";
  let element = document.head.querySelector(`#${elementId}`);

  if (!serializedStructuredData) {
    if (element) element.remove();
    return;
  }

  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.id = elementId;
    document.head.appendChild(element);
  }

  element.textContent = serializedStructuredData;
};

function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  robots = DEFAULT_ROBOTS,
  type = "website",
  canonicalPath,
  structuredData,
}) {
  const location = useLocation();

  const canonicalUrl = useMemo(() => {
    const routePath = canonicalPath || `${location.pathname}${location.search}`;
    return toAbsoluteUrl(routePath);
  }, [canonicalPath, location.pathname, location.search]);

  const imageUrl = useMemo(() => toAbsoluteUrl(image || DEFAULT_IMAGE), [image]);
  const serializedStructuredData = useMemo(() => {
    if (!structuredData) return "";
    // A bare JSON array has no top-level "@context", which trips up scrapers
    // that expect a single object (e.g. `parsed["@context"]`). Wrap multiple
    // nodes in the spec's @graph pattern so there's always one shared context.
    const payload = Array.isArray(structuredData)
      ? { "@context": "https://schema.org", "@graph": structuredData }
      : structuredData;
    return JSON.stringify(payload);
  }, [structuredData]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:site_name", "ZealFC");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertLink("canonical", canonicalUrl);
    upsertStructuredData(serializedStructuredData);
  }, [canonicalUrl, description, imageUrl, robots, serializedStructuredData, title, type]);

  return null;
}

export default Seo;
